import {
  CommentType,
  DEFAULT_SHOW_IN_PLANS,
  commentTypePresets,
  getCommentType,
  getCommentTypeCapabilities,
  isDerivedCommentType,
} from '@constants'

type ShowInPlans = NonNullable<Comment3D['showInPlans']>

/**
 * Form shape for a comment's settings, shared by the `CommentDrawer` (Form2 + submitOnChange) and
 * the in-scene `CommentEditPopup` (FormProvider + save-on-close). `color` / `backgroundColor` /
 * `showInPlans` are only user-controlled for the `custom` type — the derived types overwrite them
 * from their preset on save (see `formValuesToEditedFields`).
 */
export interface CommentSettingsFormValues {
  type: CommentType
  storeysForPlans: string[]
  color: string
  backgroundColor: string
  showInPlans: ShowInPlans
  storeyForSceneRendering: string | null
  showTitle: boolean
}

/** Seed the form from a comment. */
export const commentToFormValues = (comment: Comment3D): CommentSettingsFormValues => ({
  type: getCommentType(comment),
  storeysForPlans: comment.storeysForPlans || [],
  color: comment.color,
  backgroundColor: comment.backgroundColor,
  showInPlans: comment.showInPlans ?? DEFAULT_SHOW_IN_PLANS,
  storeyForSceneRendering: comment.storeyForSceneRendering ?? null,
  showTitle: comment.showTitle,
})

/**
 * The fields to persist for the current form values. The non-custom types derive their colour and
 * (unless they expose it, e.g. Notiz) plan visibility from `commentTypePresets`.
 */
export const formValuesToEditedFields = (values: CommentSettingsFormValues): Partial<Comment3D> => {
  const { type } = values

  if (!isDerivedCommentType(type)) {
    // custom: everything is user-controlled.
    return {
      type,
      color: values.color,
      backgroundColor: values.backgroundColor,
      storeysForPlans: values.storeysForPlans,
      showInPlans: values.showInPlans,
      storeyForSceneRendering: values.storeyForSceneRendering,
      showTitle: values.showTitle,
    }
  }

  const preset = commentTypePresets[type]
  return {
    type,
    color: preset.color,
    backgroundColor: preset.backgroundColor,
    storeysForPlans: values.storeysForPlans,
    // e.g. Notiz keeps its derived color but lets the user choose plan visibility.
    showInPlans: getCommentTypeCapabilities(type).showInPlans
      ? values.showInPlans
      : preset.showInPlans,
  }
}
